import { Lightbulb } from 'lucide-react'

import { type TargetBreakdown } from '@/api/types/nutrition'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

type NutritionTipsPopoverProps = {
  weightKg: number
  breakdown?: TargetBreakdown | null
}

export function NutritionTipsPopover({ weightKg, breakdown }: NutritionTipsPopoverProps) {
  const effectiveGoal = breakdown?.effective_goal ?? breakdown?.goal ?? 'lean_bulk'

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="inline-flex items-center gap-0.5 rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-accent">
          <Lightbulb className="size-3" />
          <span>ガイド</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-3 text-xs" align="end">
        {effectiveGoal === 'cut' ? (
          <CutGuide weightKg={weightKg} breakdown={breakdown} />
        ) : (
          <LeanBulkGuide weightKg={weightKg} />
        )}
      </PopoverContent>
    </Popover>
  )
}

function LeanBulkGuide({ weightKg }: { weightKg: number }) {
  const proteinMin = (weightKg * 1.62).toFixed(0)
  const proteinMax = (weightKg * 2.2).toFixed(0)
  const perMealMin = (weightKg * 0.4).toFixed(0)
  const perMealMax = (weightKg * 0.55).toFixed(0)

  return (
    <>
      <p className="font-medium text-sm">💡 リーンバルク栄養ガイド</p>
      <div>
        <p className="mb-0.5 font-medium">たんぱく質目標</p>
        <p>
          1日:{' '}
          <span className="font-semibold text-blue-400">
            {proteinMin}〜{proteinMax}g
          </span>
          （体重×1.62〜2.2g）
        </p>
        <p>
          1食:{' '}
          <span className="font-semibold text-blue-400">
            {perMealMin}〜{perMealMax}g
          </span>
          （体重×0.4〜0.55g、最低25g）
        </p>
      </div>
      <div>
        <p className="mb-0.5 font-medium">ロイシン & MPS</p>
        <p>MPS最大化にはロイシン2〜3gが閾値。鶏胸肉100gで約1.7g、ホエイ25gで約2.5g</p>
        <p className="mt-0.5">MPS持続は約5〜6時間 → 朝食〜夕食間で間隔を空けすぎない</p>
      </div>
      <div>
        <p className="mb-0.5 font-medium">PFCの考え方</p>
        <p>P: 範囲内で固定 / F: 体重×1.0g固定 / C: 残りカロリーの調整弁</p>
      </div>
    </>
  )
}

function CutGuide({ weightKg, breakdown }: { weightKg: number; breakdown?: TargetBreakdown | null }) {
  const proteinPerKg = breakdown?.protein_g_per_kg ?? 2.2
  const proteinTarget = (weightKg * proteinPerKg).toFixed(0)
  const cutRate = breakdown?.cut_rate_pct
  const bodyFat = breakdown?.body_fat

  return (
    <>
      <p className="font-medium text-sm">🔥 減量フェーズガイド</p>
      {bodyFat != null && (
        <div>
          <p className="mb-0.5 font-medium">現在のフェーズ</p>
          <p>
            体脂肪率: <span className="font-semibold text-orange-400">{bodyFat.toFixed(1)}%</span>
          </p>
          {cutRate != null && (
            <p>
              減量ペース: <span className="font-semibold text-orange-400">週{cutRate.toFixed(1)}%</span>（
              {((weightKg * cutRate) / 100).toFixed(2)}kg/週）
            </p>
          )}
        </div>
      )}
      <div>
        <p className="mb-0.5 font-medium">たんぱく質目標</p>
        <p>
          1日: <span className="font-semibold text-blue-400">{proteinTarget}g</span>
          （体重×{proteinPerKg}g）
        </p>
        <p className="text-muted-foreground mt-0.5">体脂肪率が低いほど高タンパク（2.2〜2.8g/kg）で筋量維持</p>
      </div>
      <div>
        <p className="mb-0.5 font-medium">PFCの考え方</p>
        <p>P: 体脂肪連動で固定 / F: 体重×1.0g固定 / C: 残りカロリーの調整弁</p>
      </div>
    </>
  )
}
